import React, { useEffect, useState } from 'react';
import { BookOpen, X, Minus, Plus } from 'lucide-react';
import { TheoryMarkdown } from './TheoryMarkdown';
import { fetchLessonTheory } from '../lib/supabase';
import { getSavedTheoryFontSize, saveTheoryFontSize } from '../lib/storage';
import { translations } from '../lib/i18n';
import { useData } from '../context/DataContext';

interface TheoryPanelProps {
  lessonId: string;
  isMobile: boolean;
  onClose: () => void;
}

export const TheoryPanel: React.FC<TheoryPanelProps> = ({ lessonId, isMobile, onClose }) => {
  const { language } = useData();
  const t = translations[language];

  const [theoryContent, setTheoryContent] = useState<string | null>(null);
  const [lessonName, setLessonName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [fontSize, setFontSize] = useState<number>(getSavedTheoryFontSize);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    fetchLessonTheory(lessonId)
      .then((data) => {
        if (!isMounted) return;
        setLessonName(data.name);
        setTheoryContent(data.theory);
      })
      .catch((err) => {
        if (!isMounted) return;
        console.error('Error fetching theory:', err);
        setError(err.message || 'Failed to load theory');
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [lessonId]);

  // Lock body scroll on mobile
  useEffect(() => {
    if (isMobile) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isMobile]);

  // Esc key closes panel
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleDecreaseFont = () => {
    const next = Math.max(13, fontSize - 1);
    setFontSize(next);
    saveTheoryFontSize(next);
  };

  const handleIncreaseFont = () => {
    const next = Math.min(24, fontSize + 1);
    setFontSize(next);
    saveTheoryFontSize(next);
  };

  const headerContent = (
    <div className="theory-panel-header">
      <div className="theory-panel-title-wrap">
        <BookOpen size={18} aria-hidden="true" />
        <span className="theory-panel-title">{lessonName || t.theoryTitle}</span>
      </div>

      <div className="theory-font-controls">
        <button
          type="button"
          className="font-size-btn"
          onClick={handleDecreaseFont}
          title={t.fontSmaller}
          aria-label={t.fontSmaller}
          disabled={fontSize <= 13}
        >
          <Minus size={13} />
        </button>
        <span style={{ fontSize: '0.75rem', fontWeight: 700, paddingInline: '0.2rem' }}>
          {fontSize}px
        </span>
        <button
          type="button"
          className="font-size-btn"
          onClick={handleIncreaseFont}
          title={t.fontLarger}
          aria-label={t.fontLarger}
          disabled={fontSize >= 24}
        >
          <Plus size={13} />
        </button>

        <button
          type="button"
          className="font-size-btn"
          onClick={() => {
            window.location.href = `/lesson/${lessonId}`;
          }}
          title={language === 'ar' ? 'فتح في وضع الرسم والتعديل' : 'Ouvrir en mode dessin'}
          aria-label="Studio"
        >
          <BookOpen size={13} />
        </button>

        <button
          type="button"
          className="icon-action-btn"
          style={{ width: '30px', height: '30px', marginInlineStart: '0.5rem' }}
          onClick={onClose}
          title={t.theoryClose}
          aria-label={t.theoryClose}
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );

  const bodyContent = (
    <div
      className="theory-panel-scroll-body"
      style={{ fontSize: `${fontSize}px` }}
      dir="auto"
    >
      {loading && (
        <div className="state-box" style={{ margin: '1rem 0', padding: '1.5rem' }}>
          <p className="state-desc">{t.loadingData}</p>
        </div>
      )}

      {error && (
        <div className="state-box" style={{ margin: '1rem 0', padding: '1.5rem' }}>
          <p className="state-title">{t.loadingErrorTitle}</p>
          <p className="state-desc">{error}</p>
        </div>
      )}

      {!loading && !error && (!theoryContent || !theoryContent.trim()) && (
        <div className="state-box" style={{ margin: '1rem 0', padding: '1.5rem' }}>
          <p className="state-desc">{t.noLessonsFound}</p>
        </div>
      )}

      {!loading && !error && theoryContent && theoryContent.trim() && (
        <TheoryMarkdown content={theoryContent} />
      )}
    </div>
  );

  if (isMobile) {
    return (
      <div className="theory-sheet-backdrop" onClick={onClose} role="dialog" aria-modal="true">
        <div className="theory-sheet-container" onClick={(e) => e.stopPropagation()}>
          {headerContent}
          {bodyContent}
        </div>
      </div>
    );
  }

  return (
    <aside className="theory-desktop-card" aria-label={t.theoryTitle}>
      {headerContent}
      {bodyContent}
    </aside>
  );
};
